import { useEffect, useId, useRef } from "react";

function GoogleG({ className = "" }: { className?: string }) {
  return (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M23.5 12.28c0-.86-.08-1.68-.22-2.48H12v4.7h6.45c-.28 1.48-1.12 2.73-2.38 3.57v2.98h3.85c2.25-2.08 3.55-5.14 3.55-8.77z"
        fill="#4285F4"
      />
      <path
        d="M12 24c3.24 0 5.96-1.08 7.94-2.91l-3.85-2.98c-1.08.72-2.45 1.15-4.09 1.15-3.15 0-5.82-2.12-6.77-4.98H1.27v3.08C3.23 21.32 7.31 24 12 24z"
        fill="#34A853"
      />
      <path
        d="M5.23 14.28c-.25-.72-.38-1.49-.38-2.28s.14-1.56.38-2.28V6.64H1.27C.46 8.24 0 10.06 0 12s.46 3.76 1.27 5.36l3.96-3.08z"
        fill="#FBBC05"
      />
      <path
        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.45-3.45C17.95 1.19 15.23 0 12 0 7.31 0 3.23 2.68 1.27 6.64l3.96 3.08c.95-2.86 3.62-4.97 6.77-4.97z"
        fill="#EA4335"
      />
    </svg>
  );
}

export function GooglePreferredSource({
  className = "",
  heading,
  fallbackText = "Get our research in your Google results",
  variant = "script",
  buttonText = "Make us preferred on Google",
}: {
  className?: string;
  heading?: string;
  fallbackText?: string;
  variant?: "script" | "button";
  buttonText?: string;
}) {
  const fallbackId = useId();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (variant !== "script") return;
    if (typeof document === "undefined") return;
    const src = "https://news.google.com/swg/js/v1/publisher.js";
    if (!document.querySelector(`script[src="${src}"]`)) {
      const script = document.createElement("script");
      script.async = true;
      script.src = src;
      document.head.appendChild(script);
    }

    const container = containerRef.current;
    const fallback = document.getElementById(fallbackId);
    if (!container || !fallback) return;

    const hideFallback = () => {
      if (container.children.length > 0) {
        fallback.style.display = "none";
      }
    };
    hideFallback();

    const observer = new MutationObserver(hideFallback);
    observer.observe(container, { childList: true });
    return () => observer.disconnect();
  }, [variant, fallbackId]);

  if (variant === "button") {
    return (
      <a
        href="https://www.google.com/preferences/source?q=berkhospitality.com"
        target="_blank"
        rel="noopener noreferrer"
        className={`google-preferred-button inline-flex items-center gap-2.5 ${className}`}
        style={{
          padding: "10px 16px",
          borderRadius: 9999,
          background: "rgba(0,0,0,0.35)",
          border: "1px solid rgba(251,250,246,0.22)",
          color: "#FBFAF6",
          fontFamily: '"IBM Plex Sans", "IBM Plex Sans Fallback", sans-serif',
          fontSize: 14,
          fontWeight: 500,
          lineHeight: 1,
          textDecoration: "none",
          transition: "transform .2s ease, border-color .2s ease, box-shadow .2s ease",
          willChange: "transform, box-shadow",
        }}
      >
        <GoogleG />
        <span>{buttonText}</span>
      </a>
    );
  }

  return (
    <div className={className}>
      {heading ? (
        <p style={{ fontSize: 15, lineHeight: 1.5, color: "rgba(0,0,0,0.72)" }}>
          {heading}
        </p>
      ) : null}
      <div
        ref={containerRef}
        className="google-preferred-hover inline-flex items-center justify-center"
        style={{
          minHeight: 40,
          borderRadius: 8,
          padding: "6px 10px",
          border: "1px solid rgba(251,250,246,0.22)",
          background: "rgba(251,250,246,0.06)",
        }}
      >
        <div google-add-preferred-source-btn="true"></div>
      </div>
      <p id={fallbackId} className="mt-2" style={{ fontSize: 14, margin: 0 }}>
        <a
          href="https://www.google.com/preferences/source?q=berkhospitality.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "rgba(251,250,246,0.85)", textDecoration: "underline" }}
        >
          {fallbackText}
        </a>
      </p>
    </div>
  );
}
